// El último guardado cuando la página se va (recargar, cerrar la pestaña) —
// ADR-2026-11-03-el-ultimo-guardado-sobrevive-al-cierre.
//
// Al descargarse la página el navegador CANCELA los pedidos en vuelo: el guardado que
// `useGuardadoDelProyecto` dispara en `pagehide` salía y moría, y lo de los últimos 3 s
// se perdía (se vio probando: un clip soltado y la página recargada no volvía). La salida
// estándar es `fetch(..., { keepalive: true })`: el navegador lo termina de mandar aunque
// la página ya no exista. supabase-js no deja pedirlo por pedido, así que este guardado va
// directo a la API REST de Supabase con el MISMO token de la sesión (RLS igual que siempre).
//
// `keepalive` tiene un tope de 64 KB por pedido (entre todos los que estén en vuelo): un
// proyecto más pesado (transcripts, miniaturas de animaciones) no entra y queda el
// camino de siempre, que con suerte llega.

/** Margen debajo de los 64 KB que permite el navegador. */
export const TOPE_KEEPALIVE_BYTES = 60_000;

export function cabeEnKeepalive(cuerpo: string): boolean {
  return new TextEncoder().encode(cuerpo).length <= TOPE_KEEPALIVE_BYTES;
}

/**
 * Manda el PATCH del proyecto con `keepalive`. Devuelve la respuesta (una promesa) si
 * salió, o `null` si no se pudo armar (sin sesión, sin datos o demasiado pesado) y hay
 * que usar el camino de siempre. Si la página sigue viva (sólo se ocultó), quien llama
 * puede leer la respuesta: trae las filas guardadas con su `updated_at`
 * (review Greptile #242: la versión conocida avanzaba aunque el guardado fallara).
 */
export function guardarConKeepalive(p: {
  supabaseUrl: string | undefined;
  anonKey: string | undefined;
  token: string | null;
  projectId: string;
  fila: Record<string, unknown>;
  /** El `updated_at` que se conoce: si otra pestaña guardó después, este PATCH no
   *  pisa nada (ADR-2026-11-03-dos-pestanas-no-se-pisan). null = sin condición. */
  updatedAtConocido?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<Response> | null {
  if (!p.supabaseUrl || !p.anonKey || !p.token) return null;
  const cuerpo = JSON.stringify(p.fila);
  if (!cabeEnKeepalive(cuerpo)) return null;
  const f = p.fetchImpl ?? fetch;
  try {
    const condicion = p.updatedAtConocido
      ? `&updated_at=eq.${encodeURIComponent(p.updatedAtConocido)}`
      : '';
    return f(
      `${p.supabaseUrl}/rest/v1/video_projects?id=eq.${encodeURIComponent(p.projectId)}${condicion}&select=updated_at`,
      {
        method: 'PATCH',
        keepalive: true,
        headers: {
          apikey: p.anonKey,
          Authorization: `Bearer ${p.token}`,
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: cuerpo,
      },
    );
  } catch {
    return null;
  }
}
